import { snakeToCamel, weekKey } from './config.js';
import { state, patchTask, removeTask } from './state.js';

// ══════════════════════════════════════════════════════════
// AUTH
// ══════════════════════════════════════════════════════════
export function showLoginOverlay() {
  document.getElementById('auth-overlay').style.display = 'flex';
}

export function hideLoginOverlay() {
  document.getElementById('auth-overlay').style.display = 'none';
}

export async function checkAuth() {
  const resp = await fetch('/auth/me');
  if (!resp.ok) { showLoginOverlay(); return false; }
  state.currentUser = await resp.json();
  hideLoginOverlay();
  return true;
}

// ══════════════════════════════════════════════════════════
// API CORE
// ══════════════════════════════════════════════════════════
export async function apiFetch(url, opts = {}) {
  const resp = await fetch(url, { headers: { 'Content-Type': 'application/json' }, ...opts });
  if (resp.status === 401) { showLoginOverlay(); return null; }
  return resp;
}

// ══════════════════════════════════════════════════════════
// BLOCKS
// ══════════════════════════════════════════════════════════
export async function loadWeekBlocks(wk) {
  const resp = await apiFetch(`/api/blocks?week=${wk}`);
  if (!resp || !resp.ok) return;
  const rows = await resp.json();
  state.db[wk] = rows.map(snakeToCamel);
}

export async function saveBlockAPI(block) {
  const resp = await apiFetch('/api/blocks', {
    method: 'POST',
    body: JSON.stringify({
      id: block.id, weekKey: weekKey(state.currentMonday), day: block.day,
      startSlot: block.startSlot, dur: block.dur, title: block.title,
      color: block.color, notes: block.notes, tipusId: block.tipusId,
      energia: block.energia, templateId: block.templateId,
      taskId: block.taskIds?.[0] || null,
    }),
  });
  return resp?.ok ? resp.json() : null;
}

export async function updateBlockAPI(id, patch) {
  const mapped = {
    day: patch.day, startSlot: patch.startSlot, dur: patch.dur,
    title: patch.title, color: patch.color, notes: patch.notes,
    tipusId: patch.tipusId, energia: patch.energia, templateId: patch.templateId,
    done: patch.done, doneAt: patch.doneAt, calendarEventId: patch.calendarEventId,
  };
  await apiFetch(`/api/blocks/${id}`, { method: 'PUT', body: JSON.stringify(mapped) });
}

export async function deleteBlockAPI(id) {
  await apiFetch(`/api/blocks/${id}`, { method: 'DELETE' });
}

export async function toggleDoneAPI(id) {
  const resp = await apiFetch(`/api/blocks/${id}/done`, { method: 'POST' });
  return resp?.ok ? resp.json() : null;
}

export async function assignTaskToBlockAPI(blockId, taskId) {
  const resp = await apiFetch(`/api/blocks/${blockId}/tasks`, {
    method: 'POST', body: JSON.stringify({ taskId }),
  });
  return resp?.ok ? resp.json() : null;
}

// ══════════════════════════════════════════════════════════
// TEMPLATES
// ══════════════════════════════════════════════════════════
export async function loadTemplatesAPI() {
  const resp = await apiFetch('/api/templates');
  if (!resp || !resp.ok) return;
  const rows = await resp.json();
  state.templates = rows.map(t => ({
    id: t.id, title: t.title, days: t.days,
    startSlot: t.start_slot, dur: t.dur,
    color: t.color, notes: t.notes,
    tipusId: t.tipus_id, energia: t.energia,
  }));
  state.exceptions = {};
  rows.forEach(t => (t.exceptions || []).forEach(d => { state.exceptions[`${t.id}|${d}`] = true; }));
}

export async function saveTemplateAPI(tpl, isNew = false) {
  const body = {
    id: tpl.id, title: tpl.title, days: tpl.days,
    startSlot: tpl.startSlot, dur: tpl.dur,
    color: tpl.color, notes: tpl.notes,
    tipusId: tpl.tipusId, energia: tpl.energia,
  };
  const url    = isNew ? '/api/templates' : `/api/templates/${tpl.id}`;
  const method = isNew ? 'POST' : 'PUT';
  await apiFetch(url, { method, body: JSON.stringify(body) });
}

export async function deleteTemplateAPI(id) {
  await apiFetch(`/api/templates/${id}`, { method: 'DELETE' });
}

export async function addExceptionAPI(tplId, date) {
  await apiFetch(`/api/templates/${tplId}/exceptions`, {
    method: 'POST', body: JSON.stringify({ date }),
  });
}

// ══════════════════════════════════════════════════════════
// BLOCK TYPES
// ══════════════════════════════════════════════════════════
export async function loadBlockTypesAPI() {
  const resp = await apiFetch('/api/block-types');
  if (!resp || !resp.ok) return;
  state.blockTypes = await resp.json();
}

// ══════════════════════════════════════════════════════════
// TASKS (local inbox)
// ══════════════════════════════════════════════════════════
export async function loadTasksAPI() {
  const resp = await apiFetch('/api/tasks');
  if (!resp || !resp.ok) return;
  state.tasks = await resp.json();
}

export async function createTaskAPI({ title, priority = 'baix', area = '', block_type_id = null, description = null, deadline = null, parent_id = null }) {
  const resp = await apiFetch('/api/tasks', {
    method: 'POST',
    body: JSON.stringify({ title, priority, area, block_type_id, description, deadline, parent_id }),
  });
  if (!resp?.ok) return null;
  const task = await resp.json();
  state.tasks.unshift(task);
  return task;
}

export async function updateTaskAPI(id, patch) {
  const resp = await apiFetch(`/api/tasks/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  if (!resp?.ok) return null;
  const updated = await resp.json();
  patchTask(id, updated);
  return updated;
}

export async function deleteTaskAPI(id) {
  const resp = await apiFetch(`/api/tasks/${id}`, { method: 'DELETE' });
  if (!resp?.ok) return null;
  removeTask(id);
  return true;
}

// ══════════════════════════════════════════════════════════
// TAGS (reusable per-user labels)
// ══════════════════════════════════════════════════════════
export async function loadTagsAPI() {
  const resp = await apiFetch('/api/tags');
  if (!resp || !resp.ok) return;
  state.tags = await resp.json();
}

export async function createTagAPI({ name, color = 'sky' }) {
  const resp = await apiFetch('/api/tags', {
    method: 'POST',
    body: JSON.stringify({ name, color }),
  });
  if (!resp?.ok) return null;
  const tag = await resp.json();
  state.tags.push(tag);
  return tag;
}

export async function updateTagAPI(id, patch) {
  const resp = await apiFetch(`/api/tags/${id}`, { method: 'PUT', body: JSON.stringify(patch) });
  if (!resp?.ok) return null;
  const updated = await resp.json();
  const i = state.tags.findIndex(t => t.id === id);
  if (i !== -1) state.tags[i] = updated;
  return updated;
}

export async function deleteTagAPI(id) {
  const resp = await apiFetch(`/api/tags/${id}`, { method: 'DELETE' });
  if (!resp?.ok) return null;
  state.tags = state.tags.filter(t => t.id !== id);
  return true;
}

// ══════════════════════════════════════════════════════════
// CALENDAR EVENTS
// ══════════════════════════════════════════════════════════
export async function loadCalendarEventsAPI(wk) {
  const resp = await apiFetch(`/api/calendar?week=${wk}`);
  if (!resp || !resp.ok) { state.calendarEvents = []; state.allDayEvents = []; return; }
  const all = await resp.json();
  state.calendarEvents = all.filter(e => !e.isAllDay);
  state.allDayEvents   = all.filter(e => e.isAllDay);
}

export async function loadGoogleCalendarsAPI() {
  const resp = await apiFetch('/api/calendar/calendars');
  if (!resp || !resp.ok) return;
  state.allGoogleCalendars = await resp.json();
}

export async function loadSelectedCalendarsAPI() {
  const resp = await apiFetch('/api/calendar/selected');
  if (!resp || !resp.ok) return;
  state.selectedCalendarIds = await resp.json();
}

export async function saveSelectedCalendarsAPI(ids) {
  await apiFetch('/api/calendar/selected', {
    method: 'PUT',
    body: JSON.stringify({ calendarIds: ids }),
  });
}

export async function getTimeblockingCalendarAPI() {
  const resp = await apiFetch('/api/calendar/timeblocking-calendar');
  return resp?.ok ? resp.json() : null;
}

export async function pushBlockToCalendar(blockId) {
  const resp = await apiFetch('/api/calendar/events', {
    method: 'POST',
    body: JSON.stringify({ blockId }),
  });
  return resp?.ok ? resp.json() : null;
}

export async function updateBlockInCalendar(eventId, blockId) {
  await apiFetch(`/api/calendar/events/${eventId}`, {
    method: 'PUT',
    body: JSON.stringify({ blockId }),
  });
}

export async function deleteBlockFromCalendar(eventId) {
  await apiFetch(`/api/calendar/events/${eventId}`, { method: 'DELETE' });
}

// ══════════════════════════════════════════════════════════
// TIME ENTRIES (time tracking)
// ══════════════════════════════════════════════════════════
export async function getRunningEntryAPI() {
  const resp = await apiFetch('/api/time-entries/running');
  if (!resp || !resp.ok) return null;
  return resp.json();  // entry or null
}

export async function startTimerAPI(taskId, note = null) {
  const resp = await apiFetch('/api/time-entries/start', {
    method: 'POST',
    body: JSON.stringify({ task_id: taskId, note }),
  });
  return resp?.ok ? resp.json() : null;
}

export async function stopTimerAPI(entryId) {
  const resp = await apiFetch(`/api/time-entries/${entryId}/stop`, { method: 'POST' });
  return resp?.ok ? resp.json() : null;
}

export async function loadTimeEntriesAPI(from, to) {
  const resp = await apiFetch(`/api/time-entries?from=${from}&to=${to}`);
  if (!resp || !resp.ok) { state.timeEntries = []; return []; }
  state.timeEntries = await resp.json();
  return state.timeEntries;
}

export async function loadTaskEntriesAPI(taskId) {
  const resp = await apiFetch(`/api/time-entries?task_id=${taskId}`);
  return resp?.ok ? resp.json() : [];
}

export async function createTimeEntryAPI({ task_id, started_at, ended_at = null, note = null }) {
  const resp = await apiFetch('/api/time-entries', {
    method: 'POST',
    body: JSON.stringify({ task_id, started_at, ended_at, note }),
  });
  return resp?.ok ? resp.json() : null;
}

export async function updateTimeEntryAPI(id, patch) {
  const resp = await apiFetch(`/api/time-entries/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return resp?.ok ? resp.json() : null;
}

export async function deleteTimeEntryAPI(id) {
  const resp = await apiFetch(`/api/time-entries/${id}`, { method: 'DELETE' });
  return resp?.ok ? true : null;
}

// ══════════════════════════════════════════════════════════
// COMBINED LOADERS
// ══════════════════════════════════════════════════════════
export async function loadWeekData(wk) {
  await Promise.all([
    loadWeekBlocks(wk),
    loadTasksAPI(),
    loadTagsAPI(),
    loadCalendarEventsAPI(wk),
  ]);
}

// No-op stubs — kept for compatibility with existing call sites
export function persist() {}
export function persistTemplates() {}
export function persistExceptions() {}
export async function hydrate() {}
